import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { hexToRgba } from "../utils/color";

/* Primitives partagées par le blog — toutes dérivées de la DA du site. */

/** Le label violet incliné utilisé dans les titres partout sur le site. */
export const HighlightedText = tw.span`bg-primary-500 text-gray-100 px-4 transform -skew-x-12 inline-block`;

export const Eyebrow = tw.span`uppercase tracking-wider text-sm font-bold text-primary-500`;

/** Pastille de catégorie, colorée selon la catégorie. Lien vers la catégorie, ou simple span (`linked={false}`) */
const BadgeBase = styled.span(({ $color }) => [
  tw`inline-flex items-center uppercase text-xs font-bold tracking-widest px-3 py-1 rounded-full transition duration-300 focus:outline-none focus:shadow-outline`,
  `color: ${$color}; background-color: ${hexToRgba($color, 0.12)};`,
  `&:hover, &:focus { background-color: ${hexToRgba($color, 0.22)}; }`,
]);

export const CategoryBadge = ({ category, linked = true, ...rest }) => {
  if (!category) return null;
  if (!linked) {
    return (
      <BadgeBase $color={category.color} {...rest}>
        {category.label}
      </BadgeBase>
    );
  }
  return (
    <BadgeBase as={Link} to={`/blog/categorie/${category.slug}`} $color={category.color} {...rest}>
      {category.label}
    </BadgeBase>
  );
};

/** Petite puce de tag. */
export const TagChip = styled(Link)`
  ${tw`inline-block text-xs font-semibold px-3 py-1 rounded-full bg-gray-200 text-gray-700 transition duration-300 focus:outline-none focus:shadow-outline`}
  ${tw`hocus:bg-primary-500 hocus:text-gray-100`}
`;

/** Ligne de métadonnées (date · temps de lecture). */
export const MetaRow = tw.div`flex flex-wrap items-center text-xs uppercase font-semibold tracking-wide text-gray-600`;
export const MetaItem = styled.span`
  ${tw`inline-flex items-center mr-4 last:mr-0`}
  svg {
    ${tw`w-4 h-4 mr-1 text-gray-500`}
  }
`;

/** Séparateur discret. */
export const Divider = tw.hr`border-t-2 border-gray-200 my-12`;

/** Bouton principal du site, utilisable comme lien interne. */
export const PrimaryButtonLink = styled(Link)`
  ${tw`inline-block px-8 py-3 font-bold rounded bg-primary-500 text-gray-100 hocus:bg-primary-700 hocus:text-gray-200 focus:shadow-outline focus:outline-none transition duration-300 text-center`}
`;

export const SecondaryButtonLink = styled(Link)`
  ${tw`inline-block px-8 py-3 font-bold rounded bg-gray-200 text-gray-800 hocus:bg-gray-300 hocus:text-gray-900 focus:shadow-outline focus:outline-none transition duration-300 text-center`}
`;

/** Lien texte + flèche. */
export const ArrowLink = styled(Link)`
  ${tw`inline-flex items-center font-bold text-primary-500 border-b-2 border-transparent hocus:border-primary-500 hocus:text-primary-800 transition duration-300 focus:outline-none`}
  svg {
    ${tw`w-4 h-4 ml-2 transition duration-300 transform`}
  }
  &:hover svg,
  &:focus svg {
    ${tw`translate-x-1`}
  }
`;

/** Accessibilité : texte visible uniquement par les lecteurs d'écran. */
export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
